<template>
  <div class="tour-activities">
    <div class="tour-activities-list">
      <div
        v-for="group in visibleGroups"
        :key="`${group.title ?? 'union'}-${group.tags.length}`"
        class="tour-activity-item"
      >
        <div
          v-if="group.title"
          class="tour-activity-item__title"
        >
          {{ group.title }}
        </div>

        <UiChips
          v-if="group.tags.length"
          v-model="selectedValues"
          multiple
          small
          :options="group.tags"
          class="tour-activity-chips-font"
          @click.stop
        />
        <div
          v-else
          class="tour-activity__nothing-found"
        >
          {{ t('nothingFound') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import { UiChips } from '@experience/core-chips-ui';
  import ru from '../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import type { GroupIn } from '@/entities/tour-activities';
  import { buildVisibleGroups } from '@/entities/tour-activities';
  import { useTourActivitiesSelection } from '@/entities/tour-activities';

  type MessageSchema = typeof ru;
  const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
    inheritLocale: true,
    messages: { ru },
  });

  interface Props {
    groups: GroupIn[];
    inputValue: string;
  }
  const props = defineProps<Props>();

  const searchInput = computed(() => props.inputValue);

  const visibleGroups = computed<GroupIn[]>(() => buildVisibleGroups(searchInput.value, props.groups));

  const { selectedValues } = useTourActivitiesSelection();
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .tour-activities-list {
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    padding-bottom: 8px;
  }

  .tour-activity-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .tour-activity-item__title {
    margin-bottom: 8px;

    @include typography.base-500-400-semi;

    @include adaptivity.tablet {
      @include typography.base-400-400-semi;
    }
  }

  .tour-activity__nothing-found {
    margin-top: 8px;
    color: var(--alias-color-text-subtle);
    text-align: center;

    @include typography.base-500-500-regular;

    @include adaptivity.mobile {
      margin-top: 0;
    }
  }

  .tour-activity-chips-font {
    :deep(.ui-chip) {
      max-width: inherit;
    }

    :deep(.ui-chip__label-container) {
      max-width: inherit;
    }

    :deep(.ui-chip__label) {
      max-width: inherit;

      @include typography.base-400-400-regular;
    }
  }
</style>
